/**
 * Buyer care on the buyer site: which "feel unique" touches are switched on
 * (Internal > Configurations > Sales and UI Configurations > Buyer care) and
 * the small client-side memory they rely on (recently viewed items).
 */
import { useEffect, useState } from "react";
import { sendAuthenticatedRequest } from "./common/functions";

export type BuyerExperience = {
  continueWhereLeftOff: boolean;
  personalWelcome: boolean;
  yourMaterialsAlerts: boolean;
  savedSpecsProfile: boolean;
  postPurchaseUpdates: boolean;
  oneClickReorder: boolean;
  reorderNudges: boolean;
  quarterlySummary: boolean;
  earlyAccessDeals: boolean;
  priorityRfq: boolean;
};

const OFF: BuyerExperience = {
  continueWhereLeftOff: false,
  personalWelcome: false,
  yourMaterialsAlerts: false,
  savedSpecsProfile: false,
  postPurchaseUpdates: false,
  oneClickReorder: false,
  reorderNudges: false,
  quarterlySummary: false,
  earlyAccessDeals: false,
  priorityRfq: false,
};

const BASE = () =>
  (process.env.REACT_APP_BF_API_BASE_URL || "/api/bf").replace(
    /\/bf\/?$/,
    "/buyer-care",
  );

let cached: { at: number; value: BuyerExperience } | null = null;
let inflight: Promise<BuyerExperience> | null = null;

export const loadBuyerExperience = async (): Promise<BuyerExperience> => {
  if (cached && Date.now() - cached.at < 60_000) return cached.value;
  if (inflight) return inflight;
  inflight = fetch(`${BASE()}/public/experience`)
    .then(async (r) => {
      if (!r.ok) return OFF;
      const data = await r.json();
      return { ...OFF, ...(data?.experience || {}) } as BuyerExperience;
    })
    .catch(() => OFF)
    .then((value) => {
      cached = { at: Date.now(), value };
      inflight = null;
      return value;
    });
  return inflight;
};

export const useBuyerExperience = (): BuyerExperience => {
  const [value, setValue] = useState<BuyerExperience>(cached?.value || OFF);
  useEffect(() => {
    let alive = true;
    loadBuyerExperience().then((v) => alive && setValue(v));
    return () => {
      alive = false;
    };
  }, []);
  return value;
};

// --- the buyer's own preferences -------------------------------------------

export type DigestFrequency = "daily" | "weekly" | "biweekly";
export type ComplianceKey =
  | "dfarsCompliant"
  | "domesticMelt"
  | "singleHeat"
  | "mtrAvailable";

export type SpecsProfile = {
  compliance: ComplianceKey[];
  standards: string[];
  tolerancesNote: string;
  preferredStates: string[];
  maxLeadTimeDays: number | null;
};

export type BuyerPrefs = {
  alerts: {
    enabled: boolean | null;
    frequency: DigestFrequency | null;
    lastSentAt: string | null;
  };
  quarterly: { enabled: boolean | null; lastSentAt: string | null };
  deals: { enabled: boolean | null };
  specs: SpecsProfile;
  updatedAt: string | null;
};

export type BuyerPreferences = {
  prefs: BuyerPrefs;
  effective: {
    alerts: { enabled: boolean; frequency: DigestFrequency };
    quarterly: boolean;
    deals: boolean;
  };
  available: {
    alerts: boolean;
    quarterly: boolean;
    deals: boolean;
    specs: boolean;
  };
  defaults: { frequency: DigestFrequency; optIn: boolean };
};

const USER_BASE = () => process.env.REACT_APP_USER_API_BASE_URL || "";

let prefsCache: { uid: string; value: BuyerPreferences } | null = null;

/** The signed-in buyer's preferences; cached per uid for the session. */
export const loadBuyerPreferences = async (
  uid: string,
  force = false,
): Promise<BuyerPreferences | null> => {
  if (!force && prefsCache && prefsCache.uid === uid) return prefsCache.value;
  try {
    const resp = await sendAuthenticatedRequest(`${USER_BASE()}/preferences`, {
      method: "GET",
    });
    if (!resp.ok) return null;
    const value = (await resp.json()) as BuyerPreferences;
    prefsCache = { uid, value };
    return value;
  } catch {
    return null;
  }
};

export const saveBuyerPreferences = async (
  uid: string,
  prefs: Partial<BuyerPrefs>,
): Promise<BuyerPreferences | null> => {
  const resp = await sendAuthenticatedRequest(`${USER_BASE()}/preferences`, {
    method: "POST",
    jsonBody: { prefs },
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || "We could not save your preferences.");
  }
  return loadBuyerPreferences(uid, true);
};

/** Cached preferences, synchronously (null until loaded once). */
export const cachedBuyerPreferences = (uid: string): BuyerPreferences | null =>
  prefsCache && prefsCache.uid === uid ? prefsCache.value : null;

/**
 * The search filters a saved specs profile implies: one flag per compliance
 * choice, plus "only items that meet the standards named in my search" when
 * the buyer has standards on file.
 */
export const specsProfileFilters = (
  specs: SpecsProfile | null | undefined,
): { filter: string; value: string }[] => {
  if (!specs) return [];
  const out: { filter: string; value: string }[] = specs.compliance.map((c) => ({ filter: c, value: "true" }));
  if (specs.standards.length) out.push({ filter: "requireStandards", value: "true" });
  return out;
};

export type Deal = {
  promotionId: string;
  supplierLabel: string;
  label: string;
  pct: number;
  startsAt: string;
  endsAt: string;
  families: string[];
  sample: { id: string; summary: string }[];
};

export const loadDeals = async (): Promise<Deal[]> => {
  try {
    const resp = await sendAuthenticatedRequest(`${USER_BASE()}/deals`, {
      method: "GET",
    });
    if (!resp.ok) return [];
    const data = await resp.json();
    return Array.isArray(data?.deals) ? (data.deals as Deal[]) : [];
  } catch {
    return [];
  }
};

// --- recently viewed items -------------------------------------------------

const RECENT_KEY = "kono.recentlyViewed";
const RECENT_MAX = 8;

export type RecentlyViewedItem = {
  id: string;
  summary: string;
  price?: { value: number; currency?: string; unit?: string } | null;
  viewedAt: string;
};

export const recentlyViewed = (): RecentlyViewedItem[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? (JSON.parse(raw) as RecentlyViewedItem[]) : [];
    return Array.isArray(list) ? list.filter((i) => i && i.id) : [];
  } catch {
    return [];
  }
};

export const rememberViewedProduct = (
  product:
    | {
        id?: string;
        summary?: string;
        price?: { value?: number; currency?: string; unit?: string } | null;
      }
    | null
    | undefined,
): void => {
  if (!product?.id) return;
  try {
    const rest = recentlyViewed().filter((i) => i.id !== product.id);
    const entry: RecentlyViewedItem = {
      id: String(product.id),
      summary: String(product.summary || "").slice(0, 200),
      price:
        product.price && typeof product.price.value === "number"
          ? {
              value: product.price.value,
              currency: product.price.currency,
              unit: product.price.unit,
            }
          : null,
      viewedAt: new Date().toISOString(),
    };
    localStorage.setItem(
      RECENT_KEY,
      JSON.stringify([entry, ...rest].slice(0, RECENT_MAX)),
    );
  } catch {
    // Storage full or blocked: the strip is a convenience, not a requirement.
  }
};
